import React, { useState, useEffect } from 'react';
import cloudApi from '../../../services/cloudApi';
import { formatSmartDate } from './utils';
import { HiOutlineArrowTopRightOnSquare, HiOutlineClock, HiOutlineBolt } from 'react-icons/hi2';

const STATUS_COLORS = {
  completed: { dot: 'bg-emerald-500', text: 'text-emerald-500' },
  failed: { dot: 'bg-red-500', text: 'text-red-400' },
  running: { dot: 'bg-amber-500', text: 'text-amber-500' },
  pending: { dot: 'bg-gray-400', text: 'text-gray-400' },
};

const TRIGGER_LABELS = {
  manual: 'Manual',
  scheduled: 'Scheduled',
  immediate: 'Immediate',
  recurring: 'Recurring',
};

const formatDuration = (run) => {
  if (!run.startedAt) return null;
  if (run.status === 'running' || run.status === 'pending') return 'In progress';

  let durationSeconds = run.durationSeconds;
  if ((!durationSeconds || durationSeconds === 0) && run.completedAt) {
    const endTime = new Date(run.completedAt);
    const startTime = new Date(run.startedAt);
    durationSeconds = Math.round((endTime - startTime) / 1000);
  }

  if (!durationSeconds || durationSeconds === 0) return null;

  if (durationSeconds < 60) return `${durationSeconds}s`;
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// Styled status dot replacing unicode characters
const StatusDot = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const isActive = status === 'running';

  return (
    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
      {isActive && (
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${colors.dot} opacity-75`} />
      )}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${colors.dot}`} />
    </span>
  );
};

const HistoricalRunsDropdown = ({
  taskId,
  currentTableId,
  historicalTablesCache,
  setHistoricalTablesCache
}) => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTables();
  }, [taskId]);

  const loadTables = async () => {
    if (historicalTablesCache[taskId]) {
      setTables(historicalTablesCache[taskId]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await cloudApi.getTablesByTaskId(taskId, { limit: 100 });
      const tableList = response.data || [];

      setHistoricalTablesCache(prev => ({
        ...prev,
        [taskId]: tableList
      }));

      setTables(tableList);
    } catch (err) {
      console.error('Failed to load extraction history:', err);
      setError(err.message || 'Failed to load extraction history');
    } finally {
      setLoading(false);
    }
  };

  // Calculate row count delta between consecutive runs
  const getRowDelta = (index) => {
    if (index >= tables.length - 1) return null;
    const current = tables[index].rowCount || 0;
    const previous = tables[index + 1].rowCount || 0;
    const diff = current - previous;
    if (diff === 0) return null;
    return diff;
  };

  return (
    <div className="border-t border-gray-200 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.02] px-4 py-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
          Extraction History
          {tables.length > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] bg-gray-200 dark:bg-white/[0.06] rounded-full text-gray-500 dark:text-gray-400 normal-case tracking-normal font-medium tabular-nums">
              {tables.length}{tables.length >= 100 ? '+' : ''}
            </span>
          )}
        </h4>
        <button
          onClick={() => {
            setHistoricalTablesCache(prev => {
              const next = { ...prev };
              delete next[taskId];
              return next;
            });
            setTables([]);
            setTimeout(loadTables, 0);
          }}
          className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-blue-400 transition-colors font-medium"
        >
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-5 w-5 border border-gray-300 dark:border-white/[0.06] border-t-cyan-400"></div>
        </div>
      ) : error ? (
        <div className="text-center py-6">
          <div className="text-red-400 text-sm mb-2">{error}</div>
          <button
            onClick={loadTables}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      ) : tables.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-500 text-sm">
          No extraction history available
        </div>
      ) : (
        <div className="relative max-h-80 overflow-y-auto custom-scrollbar">
          {tables.map((table, index) => {
            const run = table.run;
            const duration = run ? formatDuration(run) : null;
            const trigger = run ? TRIGGER_LABELS[run.triggerType] : null;
            const isLatest = table.id === currentTableId;
            const isLast = index === tables.length - 1;
            const rowDelta = getRowDelta(index);
            const status = run?.status || 'completed';

            return (
              <div key={table.id} className="relative">
                {/* Timeline connector line */}
                {!isLast && (
                  <div className="absolute left-[5px] top-7 bottom-0 w-px bg-gray-200 dark:bg-white/[0.06]" />
                )}

                <button
                  onClick={() => window.open(`/table/${table.id}`, '_blank')}
                  className={`w-full flex items-start gap-3.5 py-2.5 pr-2 rounded-lg transition-all text-left group relative
                    ${isLatest
                      ? 'bg-blue-50/60 dark:bg-blue-500/[0.06]'
                      : 'hover:bg-gray-100 dark:hover:bg-white/[0.04]'
                    }`}
                >
                  {/* Status Dot */}
                  <div className="flex-shrink-0 pt-1 pl-px">
                    <StatusDot status={status} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Date + Open Link */}
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isLatest ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'}`}>
                        {formatSmartDate(table.createdAt)}
                      </span>
                      <HiOutlineArrowTopRightOnSquare
                        className={`w-3 h-3 flex-shrink-0 transition-opacity ${
                          isLatest
                            ? 'text-blue-400 opacity-50'
                            : 'text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100'
                        }`}
                      />
                      {isLatest && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-md bg-blue-100 dark:bg-blue-500/15 text-blue-500 dark:text-blue-400 uppercase tracking-wide">
                          Latest
                        </span>
                      )}
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {/* Row count */}
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium tabular-nums">
                        {(table.rowCount || 0).toLocaleString()} {table.rowCount === 1 ? 'row' : 'rows'}
                      </span>

                      {/* Row delta */}
                      {rowDelta && (
                        <span className={`text-[10px] font-medium tabular-nums ${rowDelta > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                          {rowDelta > 0 ? '+' : ''}{rowDelta.toLocaleString()}
                        </span>
                      )}

                      {/* Duration */}
                      {duration && (
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                          <HiOutlineClock className="w-3 h-3" />
                          {duration}
                        </span>
                      )}

                      {/* Trigger */}
                      {trigger && (
                        <span className="text-[11px] text-gray-400 dark:text-gray-600 flex items-center gap-1">
                          <HiOutlineBolt className="w-3 h-3" />
                          {trigger}
                        </span>
                      )}

                      {/* Error */}
                      {run?.error && (
                        <span className="text-[11px] text-red-400/80 truncate max-w-[200px]" title={run.error}>
                          {run.error}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistoricalRunsDropdown;
